import { type DocumentNode } from 'graphql';
import { type ResultOf, type VariablesOf } from 'gql.tada';
import { getUserAndTokenFromStorage } from '@firsttable/functions';
import { isServer } from '@discover-lib/utils';
import parseErrorMessage from '@discover-lib/parseErrorMessage';
import Logger from '@/lib/logger';
import AuthError from './AuthError';
import getHeaders from './getHeaders';
import getRequestId from './getRequestId';
import makeRequest from './makeRequest';
import refreshUserToken from './refreshUserToken';

const LOG_ENABLED = false;
const logger = new Logger('GQL_CLIENT', 'LOG');
if (LOG_ENABLED) {
  logger.enable();
}

const PROFILE = !isServer() && false;

type GQLConfig<QueryType> = {
  query: QueryType;
  variables: VariablesOf<QueryType>;
  abortSignal?: AbortSignal;
  skipAuthRetry?: boolean; // Flag to prevent infinite retry loops on auth errors
} & RequestInit;

/**
 * GraphQL client that handles authentication, token refresh, and error handling
 */
export default async function gqlClient<QueryType extends DocumentNode>({
  query,
  variables,
  headers: headersInit,
  next,
  cache,
  abortSignal,
  skipAuthRetry = false,
}: GQLConfig<QueryType>): Promise<ResultOf<QueryType>> {
  // generate a unique request ID based on the operation name and variables
  const requestId = getRequestId(query, variables);

  if (PROFILE) {
    performance.mark(`${requestId}.start`);
  }

  if (LOG_ENABLED) {
    logger.log(`${requestId} - Starting GraphQL request`, {
      variables,
      headersInit: (() => {
        if (!headersInit) return undefined;
        if (typeof headersInit.entries === 'function') {
          return Object.fromEntries([...(headersInit.entries() || [])]);
        }
        return headersInit.entries;
      })(),
      skipAuthRetry,
      cache,
      next,
    });
  }

  // add the request ID to the other tags
  const additionalTags = next?.tags || [];
  const tags = [requestId, ...additionalTags];

  // get headers
  const headers = await getHeaders(requestId, headersInit);

  // send request
  const { errors, data } = await makeRequest({
    requestId,
    query,
    variables,
    headers,
    next,
    cache,
    abortSignal,
    tags,
  });

  // check for GraphQL errors
  if (errors && errors.length > 0) {
    if (LOG_ENABLED) {
      logger.log(`${requestId} - GraphQL errors detected`, errors);
    }
    const authErrors = errors.filter((error) => error.status === 'UNAUTHORIZED');
    if (authErrors.length > 0) {
      if (LOG_ENABLED) {
        logger.log(`${requestId} - Auth errors detected`, authErrors);
      }
      // Try to refresh auth token and resend request
      const { token } = getUserAndTokenFromStorage();

      // Only attempt refresh if:
      // 1. We have a token (user was logged in)
      // 2. We're on the client side (not server)
      // 3. This request hasn't already attempted a retry (prevent infinite loops)
      if (token && !isServer() && !skipAuthRetry) {
        if (LOG_ENABLED) {
          logger.log(`${requestId} - Attempting token refresh and retry`);
        }
        try {
          // Refresh the token
          await refreshUserToken(requestId, headers, abortSignal);

          // Token refreshed successfully, retry the request with new token
          if (LOG_ENABLED) {
            logger.log(`${requestId} - Token refreshed, retrying request`);
          }
          const newHeaders = await getHeaders(requestId, headersInit);
          const retryResponse = await makeRequest({
            requestId,
            query,
            variables,
            headers: newHeaders,
            next,
            cache,
            abortSignal,
            tags,
          });

          // Check for errors in retry response
          if (retryResponse.errors && retryResponse.errors.length > 0) {
            if (LOG_ENABLED) {
              logger.log(`${requestId} - Retry failed with errors`, retryResponse.errors);
            }
            throw new Error(retryResponse.errors.map(({ message }) => message).join('\n'));
          }

          if (LOG_ENABLED) {
            logger.log(`${requestId} - Retry successful`);
          }

          if (PROFILE) {
            performance.mark(`${requestId}.end`);
            performance.measure(requestId, {
              start: `${requestId}.start`,
              end: `${requestId}.end`,
            });
          }

          return retryResponse.data;
        } catch (error) {
          // Token refresh failed, throw original auth error
          if (LOG_ENABLED) {
            logger.log(`${requestId} - Token refresh failed`, { error: parseErrorMessage(error) });
          }
          throw new AuthError(authErrors[0].message);
        }
      }

      // No token, server-side, or already retried - throw auth error
      if (LOG_ENABLED) {
        logger.log(`${requestId} - Cannot retry auth error`, {
          hasToken: !!token,
          isServer: isServer(),
          skipAuthRetry,
        });
      }
      throw new AuthError(authErrors[0].message);
    } else {
      if (LOG_ENABLED) {
        logger.log(`${requestId} - Non-auth errors, throwing`, errors);
      }
      throw new Error(errors.map(({ message }) => message).join('\n'));
    }
  }

  if (LOG_ENABLED) {
    logger.log(`${requestId} - Request successful`);
  }

  if (PROFILE) {
    performance.mark(`${requestId}.end`);

    performance.measure(requestId, {
      start: `${requestId}.start`,
      end: `${requestId}.end`,
    });
  }

  return data;
}
